import PageMeta from "@/components/PageMeta";
import { useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Car,
  Check,
  Layers,
  Printer,
  ShieldCheck,
  Truck,
  Zap,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import SectionFadeIn from "@/components/SectionFadeIn";
import { getRouteState } from "@/lib/navigation";
import VehicleConfigurator, { COVERAGE_OPTIONS } from "@/components/vehicle/VehicleConfigurator";
import VehicleTypeSelector from "@/components/vehicle/VehicleTypeSelector";

/* ─── Data ─── */

const VEHICLE_TYPES = [
  {
    id: "van",
    title: "Van Branding",
    eyebrow: "Most popular route",
    description:
      "Clean, dependable graphics for commercial vans — from simple text and logo work to fuller panel coverage. Suited to tradespeople, service businesses, and mobile operators who need a professional look on the road.",
    applications: ["Tradespeople", "Service vehicles", "Mobile businesses", "Single vans"],
    icon: Truck,
  },
  {
    id: "fleet",
    title: "Fleet Graphics",
    eyebrow: "Visual consistency across vehicles",
    description:
      "Consistent branding across multiple vehicles using the same artwork, colour, and layout. Suitable for businesses needing matching panel graphics, logo placement, and contact details across their whole fleet.",
    applications: ["Multi-vehicle fleets", "Company cars", "Delivery vehicles", "Corporate identity"],
    icon: Layers,
  },
  {
    id: "decals",
    title: "Partial Wrap & Decals",
    eyebrow: "Targeted coverage",
    description:
      "Cut decals, panel graphics, and partial wraps for vehicles where specific areas need branding without covering the full surface. Cost-effective for targeted logo placement, door graphics, or rear-panel messaging.",
    applications: ["Door / panel graphics", "Rear-panel messaging", "Logo decals", "Promotional markings"],
    icon: Car,
  },
];

const GALLERY_CARDS = [
  {
    id: "van-branding",
    title: "Van Branding",
    line: "Logo, text, and contact details for working commercial vans.",
    vehicleTypeId: "van",
    icon: Truck,
    image:
      "https://images.pexels.com/photos/7363099/pexels-photo-7363099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "fleet-graphics",
    title: "Fleet Graphics",
    line: "Consistent identity across multiple vehicles and panels.",
    vehicleTypeId: "fleet",
    icon: Layers,
    image:
      "https://images.pexels.com/photos/33623774/pexels-photo-33623774.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "partial-wrap-decals",
    title: "Partial Wrap & Decals",
    line: "Targeted coverage for doors, panels, and rear-panel messaging.",
    vehicleTypeId: "decals",
    icon: Car,
    image:
      "https://static.prod-images.emergentagent.com/jobs/00dae4b3-6d98-4555-9020-2420be566521/images/5e327e6f122b6dfe11278058ff167dbe2760926669951914fc984932423892c7.png",
  },
];

function buildVehicleQuoteHref({ vehicleType, coverage, quantity, lamination, notes }) {
  const params = new URLSearchParams();
  params.set("type", "Vehicle Wrap");

  const parts = [
    `Vehicle type: ${vehicleType.title}.`,
    `Coverage: ${COVERAGE_OPTIONS.find((o) => o.id === coverage)?.label || "Advise me"}.`,
    quantity && quantity !== "1" ? `Quantity: ${quantity} vehicle${quantity !== "1" ? "s" : ""}.` : "",
    lamination ? "Please advise if lamination is suitable for this application." : "",
    notes ? `Additional notes: ${notes}.` : "",
  ].filter(Boolean);

  params.set("description", parts.join(" "));
  if (quantity) params.set("quantity", quantity);
  return `/request-quote?${params.toString()}`;
}

/* ─── Component ─── */

export default function VehicleGraphicsPage() {
  const location = useLocation();
  const configuratorRef = useRef(null);

  const [vehicleTypeId, setVehicleTypeId] = useState("van");
  const [coverage, setCoverage] = useState("panel-graphics");
  const [quantity, setQuantity] = useState("1");
  const [lamination, setLamination] = useState(false);
  const [notes, setNotes] = useState("");

  const vehicleType = useMemo(
    () => VEHICLE_TYPES.find((v) => v.id === vehicleTypeId) || VEHICLE_TYPES[0],
    [vehicleTypeId]
  );

  const quoteHref = buildVehicleQuoteHref({ vehicleType, coverage, quantity, lamination, notes });

  const handleGallerySelect = (typeId) => {
    setVehicleTypeId(typeId);
    configuratorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="py-16 md:py-24 pb-28 md:pb-24" data-testid="vehicle-graphics-page">
      <PageMeta
        title="Vehicle Graphics"
        description="Van branding, fleet graphics, and partial wraps printed on Roland TrueVIS VG-640 with optional lamination. Configure coverage and quantity — get a vehicle graphics quote."
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* ── Hero ── */}
        <SectionFadeIn>
          <section
            className="rounded-sm border border-brand-border bg-[radial-gradient(circle_at_top,_rgba(203,160,82,0.12),_transparent_32%),linear-gradient(135deg,rgba(255,255,255,0.03),rgba(10,13,20,0.96))] px-6 py-14 md:px-10 md:py-16"
            data-testid="vehicle-hero"
          >
            <div className="grid grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] gap-10 items-start">
              <div>
                <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-brand-gold">
                  Vehicle Graphics
                </p>
                <h1
                  className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-brand-text max-w-3xl"
                  data-testid="vehicle-heading"
                >
                  Vehicle graphics built for commercial visibility and a cleaner finished look
                </h1>
                <p
                  className="mt-4 max-w-2xl text-[15px] leading-relaxed text-brand-text-secondary"
                  data-testid="vehicle-hero-copy"
                >
                  Printed on Roland TrueVIS VG-640 for sharp colour and detail across vans, fleets, and promotional vehicles. Lamination available where added durability, finish protection, and easier cleaning matter.
                </p>

                <div className="mt-8 flex flex-wrap gap-3" data-testid="vehicle-hero-ctas">
                  <Link
                    to={quoteHref}
                    state={getRouteState(location)}
                    className="inline-flex items-center gap-2 rounded-sm bg-brand-gold px-7 py-3 text-[13px] font-medium tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover"
                    data-testid="vehicle-hero-quote-cta"
                  >
                    Request Vehicle Graphics Quote <ArrowRight size={15} />
                  </Link>
                  <Link
                    to="/request-quote"
                    state={getRouteState(location)}
                    className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-7 py-3 text-[13px] font-medium tracking-wide text-brand-text transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold"
                    data-testid="vehicle-hero-contact-cta"
                  >
                    Talk to Us <ArrowRight size={15} />
                  </Link>
                </div>
              </div>

              <div
                className="rounded-sm border border-white/[0.06] bg-brand-bg/70 p-5"
                data-testid="vehicle-spec-panel"
              >
                <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-brand-gold">
                  Production Specification
                </p>
                <div className="mt-4 space-y-3">
                  {[
                    {
                      icon: Printer,
                      title: "Roland VG-640 output",
                      copy: "Sharp detail and dependable colour for single vehicles, vans, and fleet graphics.",
                    },
                    {
                      icon: ShieldCheck,
                      title: "Lamination available",
                      copy: "Added finish protection for graphics that need longer life, easier cleaning, and a stronger finished look.",
                    },
                    {
                      icon: Zap,
                      title: "Application guidance included",
                      copy: "We advise the right coverage, material, and finish before quoting so there are no surprises on site.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="rounded-sm border border-white/[0.05] bg-brand-surface/40 px-4 py-4"
                      data-testid={`vehicle-spec-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 text-brand-gold">
                          <item.icon size={18} />
                        </span>
                        <div>
                          <p className="text-[13px] font-medium text-brand-text">{item.title}</p>
                          <p className="mt-1 text-[12px] leading-relaxed text-brand-text-secondary/65">
                            {item.copy}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </SectionFadeIn>

        {/* ── 3-card 16:9 gallery ── */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="vehicle-gallery-section">
            <div className="max-w-2xl">
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">
                Common vehicle graphic jobs
              </p>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-brand-text">
                Choose the route that fits your vehicle
              </h2>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {GALLERY_CARDS.map((item, index) => (
                <SectionFadeIn key={item.id} delay={index * 70}>
                  <button
                    type="button"
                    onClick={() => handleGallerySelect(item.vehicleTypeId)}
                    className="group h-full w-full overflow-hidden rounded-sm border border-brand-border bg-brand-surface/40 text-left transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-brand-gold/40"
                    data-testid={`vehicle-gallery-${item.id}`}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden bg-brand-bg">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/80 via-[#0A0D14]/15 to-transparent" />
                    </div>
                    <div className="px-5 py-5">
                      <div className="flex items-center gap-2 text-brand-gold">
                        <item.icon size={18} />
                        <span className="text-[10px] uppercase tracking-[0.18em]">
                          Vehicle graphic type
                        </span>
                      </div>
                      <h3 className="mt-4 text-[18px] font-medium text-brand-text group-hover:text-brand-gold transition-colors duration-300">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-[13px] leading-relaxed text-brand-text-secondary">
                        {item.line}
                      </p>
                      <p className="mt-4 text-[11px] uppercase tracking-[0.16em] text-brand-gold/80">
                        Use this route <ArrowRight size={12} className="inline" />
                      </p>
                    </div>
                  </button>
                </SectionFadeIn>
              ))}
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Vehicle type selector ── */}
        <VehicleTypeSelector
          types={VEHICLE_TYPES}
          activeId={vehicleTypeId}
          onSelect={setVehicleTypeId}
        />

        {/* ── Durability / lamination guidance ── */}
        <SectionFadeIn>
          <section
            className="mt-16 rounded-sm border border-brand-border bg-brand-surface/25 px-6 py-8 md:px-8"
            data-testid="vehicle-guidance-section"
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
              <div className="max-w-xl">
                <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">
                  Durability & lamination guidance
                </p>
                <h2 className="mt-3 font-heading text-3xl tracking-tight text-brand-text">
                  Graphics that stay presentable in everyday commercial use
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                {[
                  "Roland TrueVIS VG-640 printing gives sharp, consistent colour for both single vehicles and fleet runs.",
                  "Lamination adds a protective layer for graphics that need to handle cleaning, weather, and road exposure without degrading quickly.",
                  "Partial wraps and cut decals are a cost-effective route when full coverage is not needed — targeted logo or contact details work well here.",
                  "We can advise the right coverage level, lamination suitability, and finishing approach before producing so the result matches the vehicle use.",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-sm border border-white/[0.05] bg-brand-bg/70 px-4 py-4"
                    data-testid={`vehicle-guidance-${item.toLowerCase().slice(0, 24).replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span className="mt-0.5 text-brand-gold">
                      <Check size={15} />
                    </span>
                    <p className="text-[13px] leading-relaxed text-brand-text-secondary">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Configurator ── */}
        <SectionFadeIn>
          <VehicleConfigurator
            configuratorRef={configuratorRef}
            location={location}
            vehicleType={vehicleType}
            coverage={coverage}
            setCoverage={setCoverage}
            quantity={quantity}
            setQuantity={setQuantity}
            notes={notes}
            setNotes={setNotes}
            lamination={lamination}
            setLamination={setLamination}
            quoteHref={quoteHref}
          />
        </SectionFadeIn>

        {/* ── Final CTA bar ── */}
        <SectionFadeIn>
          <section
            className="mt-16 border-y border-white/[0.05] bg-brand-surface/40 px-6 py-12 md:px-8"
            data-testid="vehicle-final-cta-section"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">
                  Ready to brand your vehicle or fleet?
                </h2>
                <p className="mt-2 text-[14px] leading-relaxed text-brand-text-secondary max-w-2xl">
                  Tell us the vehicle type, coverage level, and any artwork or logo details you have. We&rsquo;ll advise the best approach and send an accurate quote before any production starts.
                </p>
              </div>
              <Link
                to={quoteHref}
                state={getRouteState(location)}
                className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-gold px-8 py-3.5 text-[13px] font-medium tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover shrink-0"
                data-testid="vehicle-final-cta-button"
              >
                Request Vehicle Graphics Quote <ArrowRight size={15} />
              </Link>
            </div>
          </section>
        </SectionFadeIn>

      </div>
    </div>
  );
}
